// Componente para gestionar jornadas en presupuestos multi-evento
import React, { useState, useMemo, useEffect } from 'react';
import {
  Calendar, Clock, Users, MapPin, Plus, Copy, Trash2,
  ChevronDown, ChevronUp, FileText, MoreVertical,
  Wand2, AlertCircle, Edit2, Save, X, UtensilsCrossed
} from 'lucide-react';
import { placesAPI, presupuestosAPI, ordenCocinaV2API, getAuthToken } from '../api/api';
import { TEXTO_ORDEN_NUEVA_PRUEBAS } from '../utils/tareasPresupuesto';
import { toast } from '../hooks/use-toast';
import DireccionAutocomplete from './DireccionAutocomplete';
import {
  TEXTO_PRECIO_EN_CABECERA, claveLinea, copiaParaJornada, lineaResuelta, personasPorDefectoLinea,
} from '../utils/multieventoCabecera';
import { distribucionMenusJornadas, textoPersonasGestorJornadas } from '../utils/multieventoPantallas';

// Helper para obtener el precio de un menú (puede estar en diferentes campos)
const getPrecioMenu = (menu, menusDisponibles = []) => {
  // Primero intentar obtener el precio de los datos guardados
  const precioGuardado = menu.precio_unitario || menu.precio_persona || menu.precio;
  if (precioGuardado && precioGuardado > 0) {
    return precioGuardado;
  }
  
  // Si no hay precio guardado, buscar en los menús disponibles por menu_id o nombre
  const menuOriginal = menusDisponibles.find(m => 
    m.id === menu.menu_id || m.nombre === menu.nombre_menu
  );
  
  if (menuOriginal) {
    return menuOriginal.precio_persona || menuOriginal.precio_unitario || menuOriginal.precio || 0;
  }
  
  return 0;
};

// Personas por defecto de una jornada nueva (D1: en un multievento la
// cabecera es la suma de las jornadas; M-98): las de la última jornada con
// personas (por fecha); sin jornadas, las del presupuesto; si no, 10
const personasPorDefecto = (presupuesto) => personasPorDefectoLinea(presupuesto, null, 10);

const JornadasManager = ({ 
  presupuesto, 
  jornadas = [], 
  onJornadaCreated,
  onJornadaUpdated,
  onJornadaDeleted,
  onGenerarJornadas,
  menusDisponibles = [],
  platosDisponibles = [],
  onRefresh,
  onGenerarOrdenCocina,
  onGenerarOrdenCocinaNueva
}) => {
  // Orden de cocina (nueva) de una jornada sin la ficha, en pruebas: la que se
  // envía sigue siendo la actual
  const descargarOrdenCocinaNuevaJornada = async (jornadaId) => {
    try {
      const respuesta = await ordenCocinaV2API.descargar(presupuesto.id, jornadaId);
      const tipo = respuesta.headers?.['content-type'] || 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([respuesta.data], { type: tipo }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let detalle = '';
      try {
        const texto = err?.response?.data?.text ? await err.response.data.text() : '';
        detalle = texto ? JSON.parse(texto)?.detail || '' : '';
      } catch {
        detalle = '';
      }
      toast({
        variant: 'destructive',
        title: 'No se pudo generar la orden de cocina (nueva)',
        description: typeof detalle === 'string' && detalle ? detalle : 'Inténtalo de nuevo.',
      });
    }
  };

  // Orden de cocina de una jornada sin la ficha: misma ruta que la ficha
  const descargarOrdenCocinaJornada = async (jornadaId) => {
    const token = getAuthToken();
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/presupuestos/${presupuesto.id}/generar-orden-cocina?jornada_id=${jornadaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        let detalle = '';
        try { detalle = (await resp.json())?.detail || ''; } catch { detalle = ''; }
        alert(typeof detalle === 'string' && detalle ? detalle : 'No se pudo generar la orden de cocina de la jornada');
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert('No se pudo generar la orden de cocina de la jornada');
    }
  };

  const [expandedJornada, setExpandedJornada] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditMenuModal, setShowEditMenuModal] = useState(null);
  const [showEditJornadaModal, setShowEditJornadaModal] = useState(null); // Modal para editar horas/personas
  const [loading, setLoading] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(null);
  
  // Estado para el modal de edición de menús
  const [menusJornadaEdit, setMenusJornadaEdit] = useState([]);
  const [savingMenus, setSavingMenus] = useState(false);
  
  // Estado para edición de jornada (horas, personas, ubicación, observaciones, transporte)
  const [editJornadaData, setEditJornadaData] = useState({
    fecha: '',
    hora_inicio: '',
    hora_fin: '',
    num_personas: '',
    lugar_evento: '',
    direccion_exacta: '',
    coordenadas: null,
    place_id: '',
    detalles_acceso: '',
    distancia_km: null,
    tiempo_viaje_min: null,
    coste_transporte: null,
    observaciones: ''
  });
  
  // Form para nueva jornada
  const [nuevaJornada, setNuevaJornada] = useState({
    fecha: '',
    hora_inicio: '09:00',
    hora_fin: '14:00',
    num_personas: personasPorDefecto(presupuesto),
    lugar_evento: '',
    observaciones: ''
  });
  
  // Form para generar automático
  const [generarConfig, setGenerarConfig] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    excluir_fines_semana: true,
    hora_inicio: '09:00',
    hora_fin: '14:00',
    num_personas: personasPorDefecto(presupuesto)
});

  // Menús del presupuesto padre (totales contratados)
  const menusContratados = useMemo(() => {
    return presupuesto?.menus_personalizados || [];
  }, [presupuesto]);

  // Fuente única: el precio, el IVA, el descuento y el concepto de cada línea
  // de jornada son los de su línea de la cabecera (Líneas y menús)
  const claveDe = (m) => claveLinea(lineaResuelta(m, menusContratados));
  const precioDeLinea = (m) => getPrecioMenu(lineaResuelta(m, menusContratados), menusDisponibles);

  // Líneas de jornada con precio o IVA distinto a la cabecera (ya no se usan)
  const [divergencias, setDivergencias] = useState(null);
  const [igualando, setIgualando] = useState(false);
  const firmaLineas = useMemo(
    () => JSON.stringify([menusContratados, jornadas.map(j => j?.menus_personalizados || [])]),
    [menusContratados, jornadas]
  );
  const presupuestoId = presupuesto?.id;
  const esMultievento = !!presupuesto?.es_multi_evento;
  useEffect(() => {
    if (!presupuestoId || !esMultievento) {
      setDivergencias(null);
      return undefined;
    }
    let vigente = true;
    presupuestosAPI.divergenciasCabeceraMultievento(presupuestoId)
      .then(({ data }) => { if (vigente) setDivergencias(data || null); })
      .catch(() => { if (vigente) setDivergencias(null); });
    return () => { vigente = false; };
  }, [presupuestoId, esMultievento, firmaLineas]);

  const igualarJornadasACabecera = async () => {
    setIgualando(true);
    try {
      const { data } = await presupuestosAPI.igualarCabeceraMultievento(presupuesto.id);
      const n = data?.cambiadas || 0;
      toast({
        title: 'Jornadas igualadas a la cabecera',
        description: n === 1 ? '1 línea actualizada.' : `${n} líneas actualizadas.`,
      });
      setDivergencias({ lineas: 0, detalle: [], aviso: null });
      onRefresh?.();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'No se han igualado las jornadas',
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setIgualando(false);
    }
  };

  // Distribución de menús en las jornadas (M-66): lo asignado en cada jornada
  // ("20 (J1) + 35 (J2) = 55"), sin "contratado" (la cabecera no significa
  // personas, D1) ni bloqueo por disponible
  const distribucionMenus = useMemo(
    () => distribucionMenusJornadas(menusContratados, jornadas),
    [menusContratados, jornadas]
  );

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${dias[fecha.getUTCDay()]} ${fecha.getUTCDate()} ${meses[fecha.getUTCMonth()]}`;
  };

  // API calls
  const handleCrearJornada = async () => {
    if (!nuevaJornada.fecha || !nuevaJornada.num_personas) {
      alert('Por favor completa los campos requeridos');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/presupuestos/${presupuesto.id}/jornadas`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...nuevaJornada, menus_personalizados: [] })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        onJornadaCreated?.(result.jornada);
        setShowAddModal(false);
        setNuevaJornada({
          fecha: '', hora_inicio: '09:00', hora_fin: '14:00',
          num_personas: personasPorDefecto(presupuesto),
          lugar_evento: '', observaciones: ''
        });
        onRefresh?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creando jornada:', error);
      alert('Error al crear la jornada');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerarJornadas = async () => {
    if (!generarConfig.fecha_inicio || !generarConfig.fecha_fin) {
      alert('Por favor selecciona las fechas de inicio y fin');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/presupuestos/${presupuesto.id}/jornadas/generar`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(generarConfig)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ ${result.message}`);
        // Las jornadas nuevas no copian el alquiler: el servidor lo avisa
        if (result.aviso) alert(result.aviso);
        // Líneas de la jornada de referencia con otro número de personas (M-48)
        if (result.aviso_lineas_distintas) {
          toast({ title: 'Revisa las líneas de las jornadas nuevas', description: result.aviso_lineas_distintas });
        }
        onGenerarJornadas?.(result);
        setShowGenerateModal(false);
        onRefresh?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error generando jornadas:', error);
      alert('Error al generar las jornadas');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicarJornada = async (jornadaId, nuevaFecha) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/presupuestos/${presupuesto.id}/jornadas/${jornadaId}/duplicar?nueva_fecha=${nuevaFecha}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        onJornadaCreated?.(result.jornada);
        // La jornada nueva no copia el alquiler de la original: el servidor lo avisa
        if (result.aviso) alert(result.aviso);
        if (result.aviso_lineas_distintas) {
          toast({ title: 'Revisa las líneas de la jornada nueva', description: result.aviso_lineas_distintas });
        }
        onRefresh?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error duplicando jornada:', error);
    } finally {
      setLoading(false);
      setMenuAbierto(null);
    }
  };

  const handleEliminarJornada = async (jornadaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta jornada?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/presupuestos/${presupuesto.id}/jornadas/${jornadaId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      
      if (response.ok) {
        onJornadaDeleted?.(jornadaId);
        onRefresh?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error eliminando jornada:', error);
    } finally {
      setLoading(false);
      setMenuAbierto(null);
    }
  };

  const handleUpdateJornada = async (jornadaId, updates) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/presupuestos/${presupuesto.id}/jornadas/${jornadaId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        onJornadaUpdated?.(result.jornada);
        onRefresh?.();
        if (result.aviso_precio) {
          toast({ title: 'Precio de la cabecera', description: result.aviso_precio });
        }
        // Cambio de hora de inicio: los servicios sin hora fija se mueven lo mismo
        if (result.servicios_desplazados > 0 || result.aviso_horas) {
          toast({
            title: result.servicios_desplazados > 0
              ? `${result.servicios_desplazados === 1 ? '1 servicio movido' : `${result.servicios_desplazados} servicios movidos`} a la nueva hora`
              : 'Revisa las horas de los servicios',
            description: result.aviso_horas || undefined,
          });
        }
        return true;
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
        return false;
      }
    } catch (error) {
      console.error('Error actualizando jornada:', error);
      alert('Error al actualizar la jornada');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Funciones del modal de edición de menús
  const openEditMenuModal = (jornadaId) => {
    const jornada = jornadas.find(j => j.id === jornadaId);
    if (jornada) {
      // El precio y el IVA no se tocan aquí: son los de la cabecera
      const menusConPrecio = (jornada.menus_personalizados || []).map(m => {
        // Buscar bebida_incluida del menú contratado si no existe en la jornada
        const menuContratado = (presupuesto?.menus_personalizados || []).find(mc => mc.menu_id === m.menu_id);
        return {
          ...m,
          bebida_incluida: m.bebida_incluida !== undefined ? m.bebida_incluida : (menuContratado?.bebida_incluida || false),
          bebidas_por_persona: m.bebidas_por_persona !== undefined ? m.bebidas_por_persona : (menuContratado?.bebidas_por_persona || 0),
          hora_inicio_servicio: m.hora_inicio_servicio || menuContratado?.hora_inicio_servicio || '',
          hora_fin_servicio: m.hora_fin_servicio || menuContratado?.hora_fin_servicio || ''
        };
      });
      setMenusJornadaEdit(menusConPrecio);
      setShowEditMenuModal(jornadaId);
    }
  };

  const closeEditMenuModal = () => {
    setShowEditMenuModal(null);
    setMenusJornadaEdit([]);
    setSavingMenus(false);
  };

  // Funciones para editar jornada (horas, personas, ubicación, observaciones)
  const openEditJornadaModal = (jornada) => {
    setEditJornadaData({
      fecha: jornada.fecha || '',
      hora_inicio: jornada.hora_inicio || '09:00',
      hora_fin: jornada.hora_fin || '14:00',
      num_personas: jornada.num_personas || '',
      // Vacío = misma dirección principal (la jornada la hereda, M-50)
      lugar_evento: jornada.lugar_evento || '',
      direccion_exacta: jornada.direccion_exacta || '',
      coordenadas: jornada.coordenadas || null,
      place_id: jornada.place_id || '',
      detalles_acceso: jornada.detalles_acceso || '',
      distancia_km: jornada.distancia_km ?? null,
      tiempo_viaje_min: jornada.tiempo_viaje_min ?? null,
      coste_transporte: jornada.coste_transporte ?? null,
      observaciones: jornada.observaciones || ''
    });
    setShowEditJornadaModal(jornada.id);
  };

  const closeEditJornadaModal = () => {
    setShowEditJornadaModal(null);
    setEditJornadaData({
      fecha: '', hora_inicio: '', hora_fin: '', num_personas: '',
      lugar_evento: '', direccion_exacta: '', coordenadas: null, place_id: '',
      detalles_acceso: '', distancia_km: null, tiempo_viaje_min: null,
      coste_transporte: null, observaciones: ''
    });
  };

  const handleSaveJornadaEdit = async () => {
    if (!showEditJornadaModal) return;

    setLoading(true);
    // Personas en un paso: la jornada y sus líneas con el mismo número, con
    // importes, tareas y cobro recalculados en el servidor
    const jornadaActual = jornadas.find(j => j.id === showEditJornadaModal);
    const personasNuevas = parseInt(editJornadaData.num_personas, 10) || 0;
    if (jornadaActual && personasNuevas > 0 && personasNuevas !== (Number(jornadaActual.num_personas) || 0)) {
      try {
        const { data } = await presupuestosAPI.cambiarPersonasJornada(presupuesto.id, showEditJornadaModal, personasNuevas, true);
        if (data?.aviso) {
          toast({ title: 'Revisa las líneas de la jornada', description: data.aviso });
        }
      } catch (error) {
        setLoading(false);
        toast({ variant: 'destructive', title: 'No se han cambiado las personas', description: error?.response?.data?.detail || error.message });
        return;
      }
    }
    // Ubicación vacía = vuelve a la dirección principal: se vacían el lugar y
    // su ruta en el servidor (`borrar`), no se guarda una copia
    const heredaLugar = !String(editJornadaData.lugar_evento || '').trim();
    const success = await handleUpdateJornada(showEditJornadaModal, heredaLugar ? {
      fecha: editJornadaData.fecha,
      hora_inicio: editJornadaData.hora_inicio,
      hora_fin: editJornadaData.hora_fin,
      detalles_acceso: editJornadaData.detalles_acceso || null,
      observaciones: editJornadaData.observaciones,
      borrar: ['lugar_evento', 'direccion_exacta', 'coordenadas', 'place_id', 'municipio',
        'distancia_km', 'tiempo_viaje_min', 'coste_transporte'],
    } : {
      fecha: editJornadaData.fecha,
      hora_inicio: editJornadaData.hora_inicio,
      hora_fin: editJornadaData.hora_fin,
      lugar_evento: editJornadaData.lugar_evento,
      direccion_exacta: editJornadaData.direccion_exacta || null,
      coordenadas: editJornadaData.coordenadas || null,
      place_id: editJornadaData.place_id || null,
      detalles_acceso: editJornadaData.detalles_acceso || null,
      distancia_km: editJornadaData.distancia_km != null ? parseFloat(editJornadaData.distancia_km) : null,
      tiempo_viaje_min: editJornadaData.tiempo_viaje_min != null ? parseInt(editJornadaData.tiempo_viaje_min) : null,
      coste_transporte: editJornadaData.coste_transporte != null ? parseFloat(editJornadaData.coste_transporte) : null,
      observaciones: editJornadaData.observaciones
    });
    setLoading(false);

    if (success) {
      closeEditJornadaModal();
    }
  };

  // ---- Selección de ubicación de jornada (autocompletado vía backend /api/places)
  // + distancia/coste de transporte desde la sede (misma fórmula que PresupuestoDetalle).
  const seleccionarLugarJornada = async (lugar) => {
    setEditJornadaData(prev => ({
      ...prev,
      lugar_evento: lugar.nombre || lugar.direccion,
      direccion_exacta: lugar.direccion,
      coordenadas: lugar.lat != null ? { lat: lugar.lat, lng: lugar.lng } : null,
      place_id: lugar.place_id || '',
    }));
    if (lugar.lat == null || lugar.lng == null) return;
    try {
      const { data } = await placesAPI.distancia(lugar.lat, lugar.lng);
      if (!data.disponible) return;
      const distanciaTotal = data.distancia_km * 2;
      const tiempoTotal = data.tiempo_min * 2;
      const costeCombustible = distanciaTotal * 0.10;
      const costePersonal = tiempoTotal * 0.26;
      setEditJornadaData(prev => ({
        ...prev,
        distancia_km: parseFloat(data.distancia_km.toFixed(2)),
        tiempo_viaje_min: data.tiempo_min,
        coste_transporte: parseFloat((costeCombustible + costePersonal).toFixed(2)),
      }));
    } catch (e) {
      console.error('Error calculando distancia jornada:', e);
    }
  };

  const handleAddMenuToJornada = (menuContratado) => {
    const key = claveLinea(menuContratado);
    const existente = menusJornadaEdit.find(m => claveDe(m) === key);

    if (existente) {
      setMenusJornadaEdit(menusJornadaEdit.map(m =>
        claveDe(m) === key
          ? {...m, num_personas: (m.num_personas || 0) + 1}
          : m
      ));
    } else {
      // La línea completa de la cabecera (precio, IVA, platos...) con 1 persona
      setMenusJornadaEdit([...menusJornadaEdit, {
        ...copiaParaJornada(menuContratado, 1),
        bebida_incluida: menuContratado.bebida_incluida || false,
        bebidas_por_persona: menuContratado.bebidas_por_persona || 0,
        hora_inicio_servicio: menuContratado.hora_inicio_servicio || '',
        hora_fin_servicio: menuContratado.hora_fin_servicio || ''
      }]);
    }
  };

  const handleUpdateMenuCantidadEdit = (menuKey, cantidad) => {
    if (cantidad <= 0) {
      setMenusJornadaEdit(menusJornadaEdit.filter(m => claveDe(m) !== menuKey));
    } else {
      // Solo cambian las personas: el precio es el de la cabecera
      setMenusJornadaEdit(menusJornadaEdit.map(m => (
        claveDe(m) === menuKey ? { ...m, num_personas: cantidad } : m
      )));
    }
  };

  const handleSaveMenusJornada = async () => {
    if (!showEditMenuModal) return;
    
    setSavingMenus(true);
    const success = await handleUpdateJornada(showEditMenuModal, {
      menus_personalizados: menusJornadaEdit
    });
    setSavingMenus(false);
    if (success) {
      closeEditMenuModal();
    }
  };

  // Calcular el importe real de una jornada basándose en sus menús
  const calcularImporteJornada = (jornada) => {
    const menus = jornada.menus_personalizados || [];
    return menus.reduce((sum, m) => {
      const precio = precioDeLinea(m);
      const personas = m.num_personas || 0;
      return sum + (precio * personas);
    }, 0);
  };

  const calcularTotales = () => {
    const totalPersonas = jornadas.reduce((sum, j) => sum + (j.num_personas || 0), 0);
    // Importe (M-66): el total con IVA del servidor (jornadas, suplementos y
    // alquiler); si no lo hay, la suma de los menús de las jornadas sin IVA
    const importeServidor = Number(presupuesto?.importe_total);
    const conIva = Number.isFinite(importeServidor) && importeServidor > 0;
    const totalImporte = conIva
      ? importeServidor
      : jornadas.reduce((sum, j) => sum + calcularImporteJornada(j), 0);
    return { totalPersonas, totalImporte, rotuloImporte: conIva ? 'total con IVA' : 'menús sin IVA' };
  };

  const { totalImporte, rotuloImporte } = calcularTotales();
  const textoPersonasTotal = textoPersonasGestorJornadas(jornadas);
  const jornadaEditActual = jornadas.find(j => j.id === showEditMenuModal);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <Calendar className="w-6 h-6 text-white" />
            <div>
              <h3 className="text-lg font-semibold text-white">
                Multi-Evento ({jornadas.length} jornadas)
              </h3>
              <p className="text-indigo-100 text-sm">
                {textoPersonasTotal} · €{totalImporte.toFixed(2)} {rotuloImporte}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center space-x-1 px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors text-sm"
              type="button"
            >
              <Wand2 className="w-4 h-4" />
              <span className="hidden sm:inline">Auto-generar</span>
            </button>
            <button
              onClick={() => {
                // Personas por defecto con las jornadas de ahora (M-98)
                setNuevaJornada(prev => ({ ...prev, num_personas: personasPorDefecto(presupuesto) }));
                setShowAddModal(true);
              }}
              className="flex items-center space-x-1 px-3 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors text-sm font-medium"
              type="button"
            >
              <Plus className="w-4 h-4" />
              <span>Añadir Jornada</span>
            </button>
          </div>
        </div>
      </div>

      {/* Fuente única: líneas de jornada con precio o IVA distinto a la cabecera */}
      {divergencias?.lineas > 0 && (
        <div className="px-4 sm:px-6 py-3 bg-amber-50 border-b border-amber-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="aviso-divergencias-cabecera">
          <p className="text-sm text-amber-800 flex items-start">
            <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>{divergencias.aviso}</span>
          </p>
          <button
            type="button"
            onClick={igualarJornadasACabecera}
            disabled={igualando}
            className="h-8 px-3 rounded-lg bg-duetto-primary text-white text-xs font-semibold hover:bg-opacity-90 disabled:opacity-50 whitespace-nowrap"
            data-testid="igualar-jornadas-cabecera"
          >
            {igualando ? 'Igualando...' : 'Igualar jornadas a la cabecera'}
          </button>
        </div>
      )}

      {/* Resumen de distribución de menús */}
      {menusContratados.length > 0 && (
        <div className="px-4 sm:px-6 py-3 bg-amber-50 border-b border-amber-100">
          <h4 className="text-xs font-medium text-amber-800 mb-2 flex items-center">
            <UtensilsCrossed className="w-3.5 h-3.5 mr-1" />
            Menús en las jornadas
          </h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(distribucionMenus).map(([key, data]) => (
              <div
                key={key}
                className={`px-2 py-1 rounded text-xs border ${
                  data.asignado > 0
                    ? 'bg-green-50 text-green-800 border-green-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200'
                }`}
              >
                <span className="font-medium">{data.nombre}</span>
                <span className="ml-1">asignado: {data.texto}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de Jornadas */}
      <div className="divide-y divide-gray-100">
        {jornadas.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay jornadas definidas</p>
            <p className="text-gray-400 text-sm mt-1">Añade jornadas manualmente o genera automáticamente</p>
          </div>
        ) : (
          jornadas.map((jornada, index) => (
            <div key={jornada.id} className="hover:bg-gray-50 transition-colors">
              <div 
                className="px-4 sm:px-6 py-3 cursor-pointer"
                onClick={() => setExpandedJornada(expandedJornada === jornada.id ? null : jornada.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-sm">
                      {index + 1}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                        <span className="font-medium text-gray-900">{formatFecha(jornada.fecha)}</span>
                        <span className="text-gray-500 text-sm flex items-center">
                          <Clock className="w-3.5 h-3.5 mr-1" />
                          {jornada.hora_inicio} - {jornada.hora_fin}
                        </span>
                        <span className="text-gray-500 text-sm flex items-center">
                          <Users className="w-3.5 h-3.5 mr-1" />
                          {jornada.num_personas} pax
                        </span>
                      </div>
                      
                      <div className="text-xs text-gray-400 mt-1 truncate">
                        {jornada.menus_personalizados?.length > 0 
                          ? jornada.menus_personalizados.map(m => `${m.nombre_menu} (${m.num_personas})`).join(', ')
                          : 'Sin menús asignados - clic para expandir'
                        }
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 sm:space-x-4">
                    <span className="font-semibold text-gray-900 text-sm sm:text-base">
                      €{calcularImporteJornada(jornada).toFixed(2)}
                    </span>
                    
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuAbierto(menuAbierto === jornada.id ? null : jornada.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        type="button"
                        data-testid={`jornada-menu-${index}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {menuAbierto === jornada.id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditJornadaModal(jornada);
                              setMenuAbierto(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            type="button"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Editar fecha/horario
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditMenuModal(jornada.id);
                              setMenuAbierto(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            type="button"
                          >
                            <UtensilsCrossed className="w-4 h-4 mr-2" />
                            Editar menús
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const nuevaFecha = prompt('Fecha para duplicar (YYYY-MM-DD):');
                              if (nuevaFecha) handleDuplicarJornada(jornada.id, nuevaFecha);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            type="button"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicar jornada
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuAbierto(null);
                              // La misma orden de cocina que la ficha (generar-orden-cocina?jornada_id=)
                              if (onGenerarOrdenCocina) {
                                onGenerarOrdenCocina(jornada.id);
                              } else {
                                descargarOrdenCocinaJornada(jornada.id);
                              }
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            type="button"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Orden de cocina
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuAbierto(null);
                              if (onGenerarOrdenCocinaNueva) {
                                onGenerarOrdenCocinaNueva(jornada.id);
                              } else {
                                descargarOrdenCocinaNuevaJornada(jornada.id);
                              }
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex flex-col items-start"
                            type="button"
                            data-testid={`jornada-orden-cocina-nueva-${jornada.id}`}
                          >
                            <span className="flex items-center">
                              <FileText className="w-4 h-4 mr-2" />
                              Orden de cocina (nueva)
                            </span>
                            <span className="pl-6 text-[11px] text-gray-500 leading-tight">{TEXTO_ORDEN_NUEVA_PRUEBAS}</span>
                          </button>
                          <hr className="my-1" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEliminarJornada(jornada.id);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                            type="button"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {expandedJornada === jornada.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
              
              {expandedJornada === jornada.id && (
                <div className="px-4 sm:px-6 pb-4 bg-gray-50 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Ubicación</label>
                      <div className="flex items-start space-x-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          {jornada.lugar_evento || presupuesto?.lugar_evento || 'Sin especificar'}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        jornada.estado === 'completado' ? 'bg-green-100 text-green-700' :
                        jornada.estado === 'en_curso' ? 'bg-blue-100 text-blue-700' :
                        jornada.estado === 'preparado' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {jornada.estado || 'pendiente'}
                      </span>
                    </div>
                    
                    <div className="sm:col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-500">Menús</label>
                        <button
                          onClick={() => openEditMenuModal(jornada.id)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center"
                          type="button"
                        >
                          <Edit2 className="w-3 h-3 mr-1" />
                          Editar menús
                        </button>
                      </div>
                      <div className="space-y-2">
                        {jornada.menus_personalizados?.length > 0 ? (
                          jornada.menus_personalizados.map((menu, idx) => {
                            const precio = precioDeLinea(menu);
                            return (
                              <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2 border border-gray-200">
                                <span className="text-sm text-gray-700">{menu.nombre_menu}</span>
                                <span className="text-sm text-gray-500">
                                  {menu.num_personas || jornada.num_personas} pax × €{precio}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center">
                            <AlertCircle className="w-4 h-4 text-amber-500 mr-2" />
                            <span className="text-sm text-amber-700">
                              Sin menús asignados. 
                              <button 
                                onClick={() => openEditMenuModal(jornada.id)}
                                className="ml-1 text-amber-800 underline hover:no-underline"
                                type="button"
                              >
                                Asignar menús
                              </button>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Observaciones - editables inline */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Observaciones</label>
                      <textarea
                        defaultValue={jornada.observaciones || ''}
                        placeholder="Instrucciones especiales, alergias, requisitos..."
                        rows={2}
                        onBlur={(e) => {
                          const newVal = e.target.value;
                          if (newVal !== (jornada.observaciones || '')) {
                            handleUpdateJornada(jornada.id, { observaciones: newVal });
                          }
                        }}
                        className="w-full text-sm text-gray-700 bg-white rounded-lg p-2 border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                        data-testid={`jornada-observaciones-${jornada.id}`}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Resumen total */}
      {jornadas.length > 0 && (
        <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{jornadas.length}</span> jornadas ·
              <span className="font-medium ml-1">{textoPersonasTotal}</span>
            </div>
            <div className="text-right">
              <span className="text-sm text-gray-500">{rotuloImporte === 'total con IVA' ? 'Total con IVA:' : 'Menús sin IVA:'}</span>
              <span className="ml-2 text-xl font-bold text-gray-900">€{totalImporte.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Modal Añadir Jornada */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Añadir Nueva Jornada</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full" type="button">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                <input
                  type="date"
                  value={nuevaJornada.fecha}
                  onChange={(e) => setNuevaJornada({...nuevaJornada, fecha: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {nuevaJornada.fecha && (
                  <p className="mt-1 text-sm text-indigo-600 font-medium">
                    📅 {new Date(nuevaJornada.fecha + 'T00:00:00').toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora inicio</label>
                  <input
                    type="time"
                    value={nuevaJornada.hora_inicio}
                    onChange={(e) => setNuevaJornada({...nuevaJornada, hora_inicio: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora fin</label>
                  <input
                    type="time"
                    value={nuevaJornada.hora_fin}
                    onChange={(e) => setNuevaJornada({...nuevaJornada, hora_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nº Personas *</label>
                <input
                  type="number"
                  value={nuevaJornada.num_personas}
                  onChange={(e) => setNuevaJornada({...nuevaJornada, num_personas: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea
                  value={nuevaJornada.observaciones}
                  onChange={(e) => setNuevaJornada({...nuevaJornada, observaciones: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" type="button">Cancelar</button>
              <button onClick={handleCrearJornada} disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50" type="button">
                {loading ? 'Creando...' : 'Crear Jornada'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Generar Automático */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowGenerateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Generar Jornadas Automáticamente</h3>
              <button onClick={() => setShowGenerateModal(false)} className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full" type="button">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-700">Se crearán jornadas para cada día del rango seleccionado.</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio *</label>
                  <input type="date" value={generarConfig.fecha_inicio} onChange={(e) => setGenerarConfig({...generarConfig, fecha_inicio: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin *</label>
                  <input type="date" value={generarConfig.fecha_fin} onChange={(e) => setGenerarConfig({...generarConfig, fecha_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora inicio</label>
                  <input type="time" value={generarConfig.hora_inicio} onChange={(e) => setGenerarConfig({...generarConfig, hora_inicio: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora fin</label>
                  <input type="time" value={generarConfig.hora_fin} onChange={(e) => setGenerarConfig({...generarConfig, hora_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Personas por jornada</label>
                <input type="number" value={generarConfig.num_personas} onChange={(e) => setGenerarConfig({...generarConfig, num_personas: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" min="1" />
              </div>
              
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={generarConfig.excluir_fines_semana} onChange={(e) => setGenerarConfig({...generarConfig, excluir_fines_semana: e.target.checked})}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                <span className="text-sm text-gray-700">Excluir fines de semana</span>
              </label>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button onClick={() => setShowGenerateModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" type="button">Cancelar</button>
              <button onClick={handleGenerarJornadas} disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50" type="button">
                {loading ? 'Generando...' : 'Generar Jornadas'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Menús de Jornada */}
      {showEditMenuModal && jornadaEditActual && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeEditMenuModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Editar Menús de Jornada</h3>
                <p className="text-sm text-gray-500">{formatFecha(jornadaEditActual.fecha)} - {jornadaEditActual.num_personas} personas</p>
              </div>
              <button onClick={closeEditMenuModal} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full" type="button">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Menús disponibles */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                  <UtensilsCrossed className="w-4 h-4 mr-2" />
                  Menús Contratados (clic para añadir)
                </h4>
                
                {menusContratados.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No hay menús contratados en este presupuesto</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {menusContratados.map((menu, idx) => {
                      const key = claveLinea(menu);
                      // M-66: siempre se puede añadir; se enseña lo ya asignado en las jornadas
                      const asignadoTexto = distribucionMenus[key]?.texto || 'sin asignar';
                      const precio = getPrecioMenu(menu, menusDisponibles);

                      return (
                        <button
                          key={idx}
                          onClick={() => handleAddMenuToJornada(menu)}
                          type="button"
                          className="p-3 rounded-lg border text-left transition-all border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer"
                        >
                          <div className="font-medium text-sm text-gray-900">{menu.nombre_menu}</div>
                          <div className="flex items-center justify-between mt-1 gap-2">
                            <span className="text-xs text-gray-500">€{precio}/pax</span>
                            <span className="text-xs text-gray-600 text-right">
                              asignado: {asignadoTexto}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Menús asignados */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Menús Asignados a Esta Jornada</h4>
                <p className="text-xs text-gray-500 mb-3" data-testid="jornada-precio-cabecera">
                  {TEXTO_PRECIO_EN_CABECERA}. Aquí solo se cambian las personas, las horas y la bebida de la jornada.
                </p>
                
                {menusJornadaEdit.length === 0 ? (
                  <p className="text-sm text-gray-400 italic p-4 text-center bg-gray-50 rounded-lg">
                    No hay menús asignados. Haz clic en los menús de arriba para añadirlos.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {menusJornadaEdit.map((menu, idx) => {
                      const key = claveDe(menu);
                      const precio = precioDeLinea(menu);
                      
                      return (
                        <div key={key || idx} className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">{menu.nombre_menu}</div>
                              <div className="text-xs text-gray-500">
                                €{precio}/pax (precio de la cabecera) · Subtotal: €{(precio * (menu.num_personas || 0)).toFixed(2)}
                              </div>
                            </div>
                          
                            <div className="flex items-center space-x-2">
                              <button onClick={() => handleUpdateMenuCantidadEdit(key, (menu.num_personas || 0) - 1)}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600" type="button">-</button>
                              <input type="number" value={menu.num_personas || 0}
                                onChange={(e) => handleUpdateMenuCantidadEdit(key, parseInt(e.target.value) || 0)}
                                className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm" min="0" />
                              <button onClick={() => handleUpdateMenuCantidadEdit(key, (menu.num_personas || 0) + 1)}
                                className="w-8 h-8 rounded-full flex items-center justify-center bg-indigo-100 hover:bg-indigo-200 text-indigo-600"
                                type="button">+</button>
                              <button onClick={() => handleUpdateMenuCantidadEdit(key, 0)}
                                className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center text-red-600 ml-2" type="button">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {/* Horario de servicio por menú */}
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                            <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-500 flex-shrink-0">Servicio:</span>
                            <input
                              type="time"
                              value={menu.hora_inicio_servicio || ''}
                              onChange={(e) => {
                                const updated = [...menusJornadaEdit];
                                // Hora puesta a mano: no se mueve al cambiar la hora de la jornada
                                updated[idx] = { ...updated[idx], hora_inicio_servicio: e.target.value, hora_servicio_manual: true };
                                setMenusJornadaEdit(updated);
                              }}
                              placeholder="Inicio"
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500"
                              data-testid={`jornada-menu-hora-inicio-${idx}`}
                            />
                            <span className="text-xs text-gray-400">-</span>
                            <input
                              type="time"
                              value={menu.hora_fin_servicio || ''}
                              onChange={(e) => {
                                const updated = [...menusJornadaEdit];
                                updated[idx] = { ...updated[idx], hora_fin_servicio: e.target.value, hora_servicio_manual: true };
                                setMenusJornadaEdit(updated);
                              }}
                              placeholder="Fin"
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500"
                              data-testid={`jornada-menu-hora-fin-${idx}`}
                            />
                          </div>
                          {/* Toggle bebida incluida por jornada */}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                            <span className="text-xs text-gray-600">Bebida incluida</span>
                            <div className="flex items-center gap-2">
                              {menu.bebida_incluida && (
                                <div className="flex items-center gap-1">
                                  <label className="text-xs text-gray-500">Cant:</label>
                                  <input
                                    type="number" min="1" max="5"
                                    value={menu.bebidas_por_persona || 1}
                                    onChange={(e) => {
                                      const updated = [...menusJornadaEdit];
                                      updated[idx] = { ...updated[idx], bebidas_por_persona: parseInt(e.target.value) || 1 };
                                      setMenusJornadaEdit(updated);
                                    }}
                                    className="w-12 px-1 py-0.5 border border-gray-300 rounded text-center text-xs"
                                  />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = [...menusJornadaEdit];
                                  const newVal = !updated[idx].bebida_incluida;
                                  updated[idx] = { ...updated[idx], bebida_incluida: newVal, bebidas_por_persona: newVal ? (updated[idx].bebidas_por_persona || 1) : 0 };
                                  setMenusJornadaEdit(updated);
                                }}
                                data-testid={`jornada-bebida-toggle-${idx}`}
                                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                                  menu.bebida_incluida ? 'bg-amber-500' : 'bg-gray-300'
                                }`}
                              >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-md transition-transform ${
                                  menu.bebida_incluida ? 'translate-x-5' : 'translate-x-0.5'
                                }`} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Resumen */}
              <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-indigo-700">Total menús en jornada:</span>
                  <span className="font-semibold text-indigo-900">
                    {menusJornadaEdit.reduce((sum, m) => sum + (m.num_personas || 0), 0)} personas
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-indigo-700">Importe estimado:</span>
                  <span className="font-semibold text-indigo-900">
                    €{menusJornadaEdit.reduce((sum, m) => sum + (getPrecioMenu(m, menusDisponibles) * (m.num_personas || 0)), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button onClick={closeEditMenuModal} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" type="button">Cancelar</button>
              <button onClick={handleSaveMenusJornada} disabled={savingMenus}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center" type="button">
                {savingMenus ? 'Guardando...' : <><Save className="w-4 h-4 mr-2" /> Guardar Cambios</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Jornada (fecha, horario, personas) */}
      {showEditJornadaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeEditJornadaModal}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <Edit2 className="w-5 h-5 mr-2 text-indigo-600" />
                Editar Jornada
              </h3>
              <button onClick={closeEditJornadaModal} className="text-gray-400 hover:text-gray-600" type="button">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Fecha */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                <input
                  type="date"
                  value={editJornadaData.fecha}
                  onChange={(e) => setEditJornadaData({...editJornadaData, fecha: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {editJornadaData.fecha && (
                  <p className="mt-1 text-sm text-indigo-600 font-medium">
                    📅 {new Date(editJornadaData.fecha + 'T00:00:00').toLocaleDateString('es-ES', { 
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                    })}
                  </p>
                )}
              </div>

              {/* Horarios */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio</label>
                  <input
                    type="time"
                    value={editJornadaData.hora_inicio}
                    onChange={(e) => setEditJornadaData({...editJornadaData, hora_inicio: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin</label>
                  <input
                    type="time"
                    value={editJornadaData.hora_fin}
                    onChange={(e) => setEditJornadaData({...editJornadaData, hora_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Personas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número de Personas</label>
                <input
                  type="number"
                  value={editJornadaData.num_personas}
                  onChange={(e) => setEditJornadaData({...editJornadaData, num_personas: e.target.value})}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Ubicación con autocomplete Google Maps */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Ubicación del evento
                </label>
                <DireccionAutocomplete
                  id="jornada-address-autocomplete"
                  value={editJornadaData.lugar_evento}
                  onChange={(lugar) => setEditJornadaData(prev => ({ ...prev, lugar_evento: lugar }))}
                  onSelect={seleccionarLugarJornada}
                  placeholder="Misma dirección principal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                {editJornadaData.direccion_exacta && (
                  <p className="mt-1 text-xs text-gray-600">{editJornadaData.direccion_exacta}</p>
                )}
                {!String(editJornadaData.lugar_evento || '').trim() && (
                  <p className="mt-1 text-xs text-gray-500" data-testid="jornada-hereda-lugar">
                    Vacío: la jornada usa la dirección principal{presupuesto?.lugar_evento ? ` (${presupuesto.lugar_evento})` : ''} y la sigue si cambia. Escribe otra solo si esta jornada es en otro sitio.
                  </p>
                )}
                {editJornadaData.distancia_km != null && (
                  <p className="mt-1 text-xs text-indigo-700">
                    🚐 {Number(editJornadaData.distancia_km).toFixed(2)} km (ida) · {editJornadaData.tiempo_viaje_min} min · transporte estimado <b>€{Number(editJornadaData.coste_transporte || 0).toFixed(2)}</b>
                  </p>
                )}
                {editJornadaData.lugar_evento && editJornadaData.lugar_evento !== presupuesto?.lugar_evento && (
                  <p className="mt-1 text-xs text-amber-600 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Ubicación diferente a la original del presupuesto
                  </p>
                )}
              </div>

              {/* Detalles de acceso (lo que Google Maps no captura) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Detalles de acceso <span className="text-xs text-gray-500 font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={editJornadaData.detalles_acceso}
                  onChange={(e) => setEditJornadaData({...editJornadaData, detalles_acceso: e.target.value})}
                  placeholder="Planta, oficina, contacto interno, código de aparcamiento…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Observaciones */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Observaciones para cocina
                </label>
                <textarea
                  value={editJornadaData.observaciones}
                  onChange={(e) => setEditJornadaData({...editJornadaData, observaciones: e.target.value})}
                  placeholder="Instrucciones especiales, alergias, requisitos de montaje..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Estas observaciones aparecerán en la orden de cocina de esta jornada
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button 
                onClick={closeEditJornadaModal} 
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" 
                type="button"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSaveJornadaEdit}
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center" 
                type="button"
              >
                {loading ? 'Guardando...' : <><Save className="w-4 h-4 mr-2" /> Guardar Cambios</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JornadasManager;
